import DataDatabase from '../classes/data-database';
import DataLayerTracker from '../classes/data-layer-tracker';
import DataPages from '../classes/data-pages';
import SeriesInfo from '../classes/series-info';
import functionalButtons from '../сonfig-and-helpers/functional-buttons';
import selectColumn from '../сonfig-and-helpers/select-column';
import HousingAffordabilityChart from './housing-affordability-chart';
import HousingAffordabilityFilter from './housing-affordability-filter-options';
import HousingAffordabilityGeoSelect from './housing-affordability-geo-select';
import HousingAffordabilityGrid from './housing-affordability-grid';
import HousingAffordabilityMap from './housing-affordability-map';
import HousingAffordabilityPeriodSelects from './housing-affordability-period-selects';

class HousingAffordability extends DataPages {
    CURRENT_DATABASE_SLUG = 'housing-affordability';

    // Define instances for various components and classes
    DatabaseDataInstance; // For working with data from a database
    PeriodSelectsInstance; // For working with period selection [ annual , monthly ]
    GridInstance; // For working with the data grid : AG Grid
    GeoSelectsInstance; // For working with geography selection
    ChartInstance; // For working with charts : AG Charts
    MapInstance; // For Map
    QueryParametersInstance; // For managing query parameters in URL
    FilterInstance; // For managing parameters in filter buttons
    SeriesInfoInstance; // For series info requests.
    DataLayerTrackerInstance; // For GTM tracker

    constructor() {
        super();
        //  store a reference to the same function that was added as the listener in order to remove it.
        this.boundPageInitialized = this.pageInitialized.bind(this);

        // Initialize and validate HTML variables
        let validationVariables = this._initAndValidateHTMLVariables();

        if (!validationVariables) {
            return;
        }

        // Register custom events
        this.registerCustomEvents();

        // Initialize instances of various components and classes
        this.initializeClassesInstances();

        // Initialize period selection
        this.initializePeriodSelectsInstance();

        // Initialize filter options
        this.initializeFilterInstance();

        // Initialize geography selection and other components when it's done
        this.initializeGeoSelectsInstance().then(() => {
            this.initializeGrid();
            this.initializeChart();
            this.initializeMap();
            this.initializeActionButtons();

            // Handle column selection using the select-all checkbox
            this._table_container.addEventListener(
                'change',
                this.onAllSelectionChanged.bind(this),
            );
        });
    }

    /**
     * Register custom events for the HousingAffordability module.
     */
    registerCustomEvents() {
        document.addEventListener(
            'gridRequest.Start',
            this.eventGirdRequestStart.bind(this),
        );

        document.addEventListener(
            'gridRequest.End',
            this.eventGirdRequestEnd.bind(this),
        );

        document.addEventListener('gridRequest.End', this.boundPageInitialized);

        document.addEventListener(
            'gridPeriod.Change',
            this.eventPeriodChange.bind(this),
        );

        document.addEventListener(
            'chartSelect.Change',
            this.eventChartSelectChangeHandler.bind(this),
        );

        document.addEventListener(
            'filterInput.Change',
            this.eventFilterChange.bind(this),
        );

        document.addEventListener(
            'resetData.Start',
            this.eventResetDataStartHandler.bind(this),
        );

        document.addEventListener(
            'resetData.End',
            this.eventResetDataEndHandler.bind(this),
        );
        document.addEventListener(
            'seriesInfoRequest.Start',
            this.seriesInfoRequestStart.bind(this),
        );
        document.addEventListener(
            'seriesInfoRequest.End',
            this.seriesInfoRequestEnd.bind(this),
        );
    }

    /**
     * Initialize instances of various classes.
     */
    initializeClassesInstances() {
        this.DatabaseDataInstance = new DataDatabase(
            this.CURRENT_DATABASE_SLUG,
        );

        this.SeriesInfoInstance = new SeriesInfo(this, 'housing-affordability');

        // initialize dataLayer tracker class
        this.DataLayerTrackerInstance = new DataLayerTracker(
            {
                state: '#area_type_1_select',
                msa: '#area_type_2_select',
                md: '#area_type_3_select',
                county: '#area_type_4_select',
                down_payment_ratio: '#filter-select',
                data_series: '[name="data-charts"]',
            },
            {
                period: '.js-period-button.a-btn--style-closed-type-01 .a-btn__label',
            },
            {},
        );
    }

    /**
     * Initialize the period selection instance.
     */
    initializePeriodSelectsInstance() {
        this.PeriodSelectsInstance = new HousingAffordabilityPeriodSelects(
            this,
        );
    }

    /**
     * Initialize the period selection instance.
     */
    initializeFilterInstance() {
        this.FilterInstance = new HousingAffordabilityFilter(this);
    }

    /**
     * Asynchronously initialize the geography selection instance.
     */
    async initializeGeoSelectsInstance() {
        const statesGeoData = await this.DatabaseDataInstance.getSelectedData();
        this.GeoSelectsInstance = new HousingAffordabilityGeoSelect(
            this,
            statesGeoData,
        );
    }

    /**
     * Initialize the data grid instance.
     */
    initializeGrid() {
        this.GridInstance = new HousingAffordabilityGrid(this);
    }

    /**
     * Initialize the chart instance.
     */
    initializeChart() {
        this.ChartInstance = new HousingAffordabilityChart(this);
    }

    /**
     * Initialize the MapImage instance.
     */
    initializeMap() {
        this.MapInstance = new HousingAffordabilityMap(this);
    }

    /**
     * Initialize action buttons for the data grid.
     */
    initializeActionButtons() {
        functionalButtons(this);
    }

    /**
     * Event handler for the 'gridRequest.Start' event.
     * Disables geography and period selection.
     */
    eventGirdRequestStart() {
        this.setLoaders();
        this.checkParams();
        this.GeoSelectsInstance.disableSelects();
        this.PeriodSelectsInstance.disableSelects();
        this.FilterInstance.disableFilter();
        this.SeriesInfoInstance.hideWrapper();

        if (this.ChartInstance instanceof HousingAffordabilityChart) {
            this.ChartInstance.resetChartOptions();
        }

        if (this.DataLayerTrackerInstance instanceof DataLayerTracker) {
            this.DataLayerTrackerInstance.pushFilters();
        }
    }

    /**
     * Event handler for the 'gridRequest.End' event.
     * Enables geography and period selection and updates chart and map data.
     * @param {Event} event - The 'gridRequest.End' event object.
     */
    eventGirdRequestEnd(event) {
        const type = event.detail.type;
        const data = event.detail.data;

        this.GeoSelectsInstance.enableSelects();
        this.PeriodSelectsInstance.enableSelects();
        this.FilterInstance.toggleFilterAvailability();
        this.GeoSelectsInstance.resetBtnAvailability(
            Boolean(data.rowData.length),
        );
        this.SeriesInfoInstance.btnAvailability(Boolean(data.rowData.length));

        if (type === 'success') {
            this.setChartData(data.rowData);
            this.setMap(data.map);
            this.GeoSelectsInstance.setQueryParameters();
        }
        // show Grid/Chart content
        this.initializationEnds(Boolean(data.rowData.length));
        // Need to resize forcefully in order to exclude the small Mapbox.
        this.MapInstance.resize();
        //
        this.removeLoaders();
    }

    /**
     * Event handler for the 'gridPeriod.Change' event.
     * Refreshes the data grid.
     */
    eventPeriodChange() {
        this.gridRefresh();
    }

    /**
     * Event handler for the 'eventFilterChange.Change' event.
     * Refreshes the data grid.
     */
    eventFilterChange() {
        this.gridRefresh();
    }

    seriesInfoRequestStart() {
        this.setLoaders();
        this.GeoSelectsInstance.disableSelects();
        this.PeriodSelectsInstance.disableSelects();
    }
    seriesInfoRequestEnd() {
        this.GeoSelectsInstance.enableSelects();
        this.PeriodSelectsInstance.enableSelects();

        this.removeLoaders();
    }
    eventResetDataStartHandler() {
        if (this.DataLayerTrackerInstance instanceof DataLayerTracker) {
            this.DataLayerTrackerInstance.forbidPushing();
        }

        this.showStartContent();
        this.SeriesInfoInstance.btnAvailability(false);
        this.SeriesInfoInstance.hideWrapper();
    }

    eventResetDataEndHandler() {
        if (this.DataLayerTrackerInstance instanceof DataLayerTracker) {
            this.DataLayerTrackerInstance.allowPushing();
            this.DataLayerTrackerInstance.pushFilters({
                reset_bnt: 'Reset Button',
            });
        }
    }

    /**
     * Event handler for the 'chartSelect.Change' event.
     * Change of chart select
     */
    eventChartSelectChangeHandler() {
        if (this.DataLayerTrackerInstance instanceof DataLayerTracker) {
            this.DataLayerTrackerInstance.pushFilters();
        }
    }

    pageInitialized() {
        super.pageInitialized();
        document.removeEventListener(
            'gridRequest.End',
            this.boundPageInitialized,
        );
        if (this.DataLayerTrackerInstance instanceof DataLayerTracker) {
            this.DataLayerTrackerInstance.allowPushing();
        }
    }

    /**
     * Get request parameters for a specific request.
     *
     * @returns {Object} The request object with parameters or `false` if missing required data.
     */
    getRequestParams() {
        let request = {};

        const geoData = this.getGeoRequestParams() ?? '';
        const period = this.getActivePeriod() ?? '';
        const filter = this.getFilter() ?? '';

        request['geoData'] = geoData;
        request['tableTypes'] = {
            period: period,
            filterId: filter,
        };

        return request;
    }

    /**
     * Get the active period from the period selection instance.
     * @returns {string} - The active period (e.g., 'annual' or 'monthly').
     */
    getActivePeriod() {
        return this.PeriodSelectsInstance.getActivePeriod();
    }

    /**
     * Get the request parameters for geography selection from the geography selection instance.
     * @returns {Object} - The request parameters for geography selection.
     */
    getGeoRequestParams() {
        return this.GeoSelectsInstance.getRequestParams();
    }

    /**
     * Get the request parameters for filter inputs.
     * @returns {Object} - The request parameters.
     */
    getFilter() {
        return this.FilterInstance.getFilter();
    }

    /**
     * Refresh the data grid.
     */
    gridRefresh() {
        if (this.GridInstance instanceof HousingAffordabilityGrid) {
            this.GridInstance.refresh();
        }
    }

    /**
     * Get the grid API instance from the data grid.
     * @returns {Object} - The grid API instance.
     */
    getGridApi() {
        return this.GridInstance.gridAPI;
    }

    /**
     * get the raw data that come from the server
     * @return {*}
     */
    getRowData() {
        return this.GridInstance.getRowData();
    }

    /**
     * Set chart data in the chart instance.
     * @param {Array} chartData - The data to be set in the chart.
     */
    setChartData(chartData) {
        this.ChartInstance.resetChartOptions();
        this.ChartInstance.setChartData(chartData);
    }

    /**
     * Set map data in the map instance.
     * @param {Object} mapData - The data to be set in the map.
     */
    setMap(mapData) {
        this.MapInstance.setMap(mapData);
    }

    /**
     * Check parameters and take specific actions based on the conditions.
     */
    checkParams() {
        this.FilterInstance.toggleFilterAvailability();
    }

    /**
     * Set the select-all checkbox to select just the cells in a column.
     * The default behavior is to select/unselect all rows.
     * @param {Event} event - The change event object.
     */
    onAllSelectionChanged(event) {
        const checkbox = event.target.closest('.ag-checkbox-input');
        if (!checkbox) {
            return;
        }
        const parent = checkbox.closest('.ag-header-cell');
        if (!parent) {
            return;
        }
        selectColumn(checkbox, this.getGridApi());
    }
}
/**
 * Export a function to initialize the HousingAffordability module on a specific page.
 */
function exportHousingAffordability() {
    let checkPage = document.querySelector('.o-data-page-single-8');

    if (checkPage) {
        new HousingAffordability();
    }
}

exportHousingAffordability();
